import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import {
  BarChart3, TrendingUp, ShoppingCart, Wallet, Users, Store,
  Factory, Award, Calendar, ArrowUp, ArrowDown,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AnalyticsDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await authFetch(`${API}/analytics/overview?period=${period}`);
      const json = await res.json();
      if (json.success) setData(json.data);
      setLoading(false);
    })();
  }, [period]);

  const fmt = (n) => {
    if (!n && n !== 0) return "0";
    if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString("en-IN");
  };

  const fmtRs = (n) => `Rs ${fmt(n / 100)}`;

  if (loading || !data) return (
    <div className="space-y-6" data-testid="analytics-page">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Analytics Dashboard</h1>
      <div className="text-center py-16"><div className="w-8 h-8 spinner mx-auto" /></div>
    </div>
  );

  const { orders, collections, dealers, employees, unit_split, top_salesmen } = data;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Sora']">Analytics Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Business intelligence and performance metrics</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 dark:bg-slate-800 border-l-4 border-l-[#8B1A1A]" data-testid="kpi-revenue">
          <div className="flex items-center gap-2 mb-1"><ShoppingCart className="w-4 h-4 text-[#8B1A1A]" /><span className="text-xs text-slate-500 uppercase tracking-wide">Revenue</span></div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{fmtRs(orders.revenue)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{orders.total} orders</p>
        </Card>
        <Card className="p-4 dark:bg-slate-800 border-l-4 border-l-emerald-500" data-testid="kpi-collections">
          <div className="flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-emerald-500" /><span className="text-xs text-slate-500 uppercase tracking-wide">Collections</span></div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{fmtRs(collections.total)}</p>
          <p className="text-xs text-slate-400 mt-0.5">{collections.count} collected</p>
        </Card>
        <Card className="p-4 dark:bg-slate-800 border-l-4 border-l-blue-500" data-testid="kpi-dealers">
          <div className="flex items-center gap-2 mb-1"><Store className="w-4 h-4 text-blue-500" /><span className="text-xs text-slate-500 uppercase tracking-wide">Active Dealers</span></div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{dealers.active}</p>
          <p className="text-xs text-slate-400 mt-0.5">{dealers.pending} pending approval</p>
        </Card>
        <Card className="p-4 dark:bg-slate-800 border-l-4 border-l-purple-500" data-testid="kpi-employees">
          <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-purple-500" /><span className="text-xs text-slate-500 uppercase tracking-wide">Field Force</span></div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{employees.present_today}/{employees.total}</p>
          <p className="text-xs text-slate-400 mt-0.5">Present today</p>
        </Card>
      </div>

      {/* Order Status + Unit Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 dark:bg-slate-800" data-testid="order-status-card">
          <h3 className="font-semibold text-sm mb-4 dark:text-white flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Order Status Breakdown</h3>
          <div className="space-y-3">
            {[
              { label: "Approved", value: orders.approved, color: "bg-emerald-500", total: orders.total },
              { label: "Pending", value: orders.pending, color: "bg-amber-500", total: orders.total },
              { label: "Cancelled", value: orders.cancelled, color: "bg-red-500", total: orders.total },
            ].map(s => (
              <div key={s.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600 dark:text-slate-400">{s.label}</span>
                  <span className="font-mono font-bold dark:text-white">{s.value}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full">
                  <div className={`h-full rounded-full ${s.color}`} style={{ width: `${s.total ? (s.value / s.total * 100) : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 dark:bg-slate-800" data-testid="unit-split-card">
          <h3 className="font-semibold text-sm mb-4 dark:text-white flex items-center gap-2"><Factory className="w-4 h-4" /> Unit-wise Orders</h3>
          <div className="flex items-end gap-8 justify-center pt-4">
            <div className="text-center">
              <div className="w-24 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden" style={{ height: "120px" }}>
                <div className="bg-sky-500 w-full rounded-lg" style={{ height: `${orders.total ? (unit_split.unit_1 / orders.total * 100) : 50}%`, marginTop: `${orders.total ? (100 - unit_split.unit_1 / orders.total * 100) : 50}%` }} />
              </div>
              <p className="text-2xl font-bold font-mono mt-2 text-sky-600">{unit_split.unit_1}</p>
              <p className="text-xs text-slate-500">Unit 1</p>
            </div>
            <div className="text-center">
              <div className="w-24 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden" style={{ height: "120px" }}>
                <div className="bg-emerald-500 w-full rounded-lg" style={{ height: `${orders.total ? (unit_split.unit_2 / orders.total * 100) : 50}%`, marginTop: `${orders.total ? (100 - unit_split.unit_2 / orders.total * 100) : 50}%` }} />
              </div>
              <p className="text-2xl font-bold font-mono mt-2 text-emerald-600">{unit_split.unit_2}</p>
              <p className="text-xs text-slate-500">Unit 2</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Salesmen */}
      <Card className="p-5 dark:bg-slate-800" data-testid="top-salesmen-card">
        <h3 className="font-semibold text-sm mb-4 dark:text-white flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Top Performing Salesmen</h3>
        {top_salesmen.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No sales data for this period</p>
        ) : (
          <div className="space-y-2">
            {top_salesmen.map((s, i) => (
              <div key={s.employee_id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900/50" data-testid={`salesman-${i}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : "bg-amber-700"}`}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium dark:text-white">{s.name}</p>
                  <p className="text-[10px] text-slate-400">{s.employee_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-mono text-[#8B1A1A]">{fmtRs(s.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AnalyticsDashboardPage;
